/**
 * Which interface language is in effect.
 *
 * Owner rule (2026-10-17): every CRM surface opens in standard Uzbek (Latin) whatever the
 * browser's language is. Only the user's own switch (kept in localStorage as `crm-lang`)
 * changes it. navigator.language is never read.
 *
 * localStorage is the single source of truth, so code outside React (the API client, the
 * auth context, utils/datetime.js) and the I18nProvider always agree on the language.
 */

export const LANG_STORAGE_KEY = 'crm-lang';

// Marks the stored language as one chosen under the Uzbek-first rule. The pre-paint script in
// public/index.html drops a crm-lang stored without it once (the old CRM wrote 'en' for everyone).
export const LANG_VERSION_KEY = 'crm-lang-v';
const LANG_VERSION = '2';

export const DEFAULT_LANG = 'uz';

/** The order every language switch uses: UZ · EN · RU. */
export const LANG_CODES = ['uz', 'en', 'ru'];

/** The code when it is a supported language, else null. */
export function asLang(value) {
  return LANG_CODES.includes(value) ? value : null;
}

// The user's choice for this tab when localStorage would not take it (site data blocked, quota
// full). It is null whenever the write worked, so the stored value stays the single truth and
// every reader (React, translate(), the date helpers) still agrees when storage is unavailable.
let memory = null;

/** The language to use right now: the stored choice, else Uzbek. */
export function getActiveLang() {
  if (memory) return memory;
  try {
    return asLang(localStorage.getItem(LANG_STORAGE_KEY)) || DEFAULT_LANG;
  } catch {
    return DEFAULT_LANG;
  }
}

/** Remember an explicit choice. Call it only from the user's own switch. */
export function rememberLang(lang) {
  const code = asLang(lang);
  if (!code) return;
  try {
    localStorage.setItem(LANG_STORAGE_KEY, code);
    localStorage.setItem(LANG_VERSION_KEY, LANG_VERSION);
    memory = null;
  } catch {
    memory = code; // storage blocked: this tab keeps the choice until it reloads
  }
}
